import { useCallback, useState } from 'react'

/**
 * What is ticked on a board, and the two ways a person changes it.
 *
 * Two boards tick rows — contacts and segments — and the rules are the same
 * whatever the rows are: a row toggles, the header box takes the page or
 * clears it, and the selection is dropped whenever the rows underneath it are
 * replaced. Written twice, the second copy is where one of those stops being
 * true, and the actions these selections lead to are deleting people and
 * deleting audiences.
 *
 * **Keyed by whatever the board's rows are identified by** — an address for a
 * contact, an id for a segment — because the only thing this needs of a row is
 * something to tell it apart from another.
 *
 * It deliberately does *not* know about "everybody a filter matches". That is
 * an action against a filter rather than a selection, only one board offers it,
 * and it is a different question with a different answer: this list is what is
 * on screen, and that one is whatever the server finds when it is asked.
 */
export function useSelection() {
  const [picked, setPicked] = useState<readonly string[]>([])

  const clear = useCallback(() => setPicked([]), [])

  const toggle = useCallback((key: string) => {
    setPicked((current) =>
      current.includes(key) ? current.filter((one) => one !== key) : [...current, key],
    )
  }, [])

  /**
   * The box in the header row: everybody on this page, or nobody.
   *
   * **This page, not every match.** Selecting forty thousand people is an
   * action against a filter rather than a selection, and a header box that
   * quietly meant "all of them" would offer that without saying so — one click
   * away from suppressing them.
   *
   * Anything ticked that is not on this page is dropped with it. That cannot
   * happen while the selection is cleared on every reload, and is the behaviour
   * this should have if it ever stops being.
   */
  const toggleAllOnPage = useCallback((keys: readonly string[]) => {
    setPicked((current) => (keys.every((key) => current.includes(key)) ? [] : [...keys]))
  }, [])

  return { picked, clear, toggle, toggleAllOnPage }
}

/**
 * Whether the header box should read as ticked.
 *
 * True only when every row on the page is, so a page with one row left unticked
 * shows the dash rather than a tick that would claim otherwise. An empty page is
 * not "all selected".
 */
export function everyRowTicked(keys: readonly string[], picked: readonly string[]): boolean {
  return keys.length > 0 && keys.every((key) => picked.includes(key))
}
